import { FormEvent, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Drawer } from '@/components/ui/Drawer';
import { useAuth } from '@/core/auth/AuthContext';
import { useProcessTemplates } from '../hooks/useProcessTemplates';
import { useProcessTemplateDetail } from '../hooks/useProcessTemplateDetail';

export function ProcessTemplateFormPage({ mode }: { mode: 'new' | 'edit' }) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { profile } = useAuth();
  const { createTemplate, updateTemplate, isCreating, isUpdating } = useProcessTemplates(profile?.company_id);
  const { template, isLoading } = useProcessTemplateDetail(mode === 'edit' ? id : undefined);
  const [showGuide, setShowGuide] = useState(false);
  const [form, setForm] = useState({
    template_code: '',
    template_name: '',
    category_code: 'construction',
    base_unit: 'cây',
    description: '',
    status_code: 'active',
  });

  useEffect(() => {
    if (template) {
      setForm({
        template_code: template.template_code,
        template_name: template.template_name,
        category_code: template.category_code,
        base_unit: template.base_unit,
        description: template.description || '',
        status_code: template.status_code || 'active',
      });
    }
  }, [template]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!profile?.company_id) return;
    const payload = {
      company_id: profile.company_id,
      template_code: form.template_code.trim() || undefined,
      template_name: form.template_name.trim(),
      category_code: form.category_code,
      base_unit: form.base_unit.trim(),
      description: form.description.trim() || null,
      status_code: form.status_code,
      created_by: profile.id,
    };

    if (mode === 'edit' && id) {
      await updateTemplate({ id, updates: payload });
      navigate(`/estimates/process-templates/${id}`);
      return;
    }

    const createdId = await createTemplate(payload);
    navigate(`/estimates/process-templates/${createdId}`);
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <div className="relative shrink-0 p-4 pr-16">
        <PageHeaderNew
          title={mode === 'edit' ? 'Sửa quy trình mẫu' : 'Tạo quy trình mẫu'}
          subtitle="Khai báo thông tin chung, sau đó thêm các bước và tài nguyên ở màn hình chi tiết"
          backUrl={mode === 'edit' && id ? `/estimates/process-templates/${id}` : '/estimates/process-templates'}
        />
        <button
          type="button"
          onClick={() => setShowGuide(true)}
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
          aria-label="Hướng dẫn sử dụng"
          title="Hướng dẫn sử dụng"
        >
          <i className="ri-question-line text-xl" />
        </button>
      </div>

      <form id="process-template-form" onSubmit={handleSubmit} className="min-h-0 flex-1 overflow-auto px-4 pb-24">
        <Card className="border-slate-200 bg-white p-4 shadow-sm">
          {isLoading && mode === 'edit' ? (
            <div className="p-6 sm:text-sm text-xs text-slate-500">Đang tải quy trình...</div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              <Field label="Mã quy trình">
                <input value={form.template_code} onChange={(event) => setForm({ ...form, template_code: event.target.value })} className="field" placeholder="Tự sinh nếu bỏ trống" />
              </Field>
              <Field label="Tên quy trình">
                <input required value={form.template_name} onChange={(event) => setForm({ ...form, template_name: event.target.value })} className="field" />
              </Field>
              <Field label="Nhóm">
                <select value={form.category_code} onChange={(event) => setForm({ ...form, category_code: event.target.value })} className="field">
                  <option value="construction">Thi công</option>
                  <option value="maintenance">Bảo dưỡng</option>
                  <option value="lighting">Chiếu sáng</option>
                  <option value="hardscape">Hardscape</option>
                  <option value="other">Khác</option>
                </select>
              </Field>
              <Field label="Đơn vị gốc">
                <input required value={form.base_unit} onChange={(event) => setForm({ ...form, base_unit: event.target.value })} className="field" />
              </Field>
              <Field label="Trạng thái">
                <select value={form.status_code} onChange={(event) => setForm({ ...form, status_code: event.target.value })} className="field">
                  <option value="active">Đang dùng</option>
                  <option value="inactive">Tạm ngưng</option>
                  <option value="archived">Lưu trữ</option>
                </select>
              </Field>
              <Field label="Mô tả" className="lg:col-span-2">
                <textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} className="field min-h-28 py-2" />
              </Field>
            </div>
          )}
        </Card>
      </form>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 p-3 backdrop-blur">
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => navigate(mode === 'edit' && id ? `/estimates/process-templates/${id}` : '/estimates/process-templates')}>Hủy</Button>
          <Button type="submit" form="process-template-form" isLoading={isCreating || isUpdating}>Lưu</Button>
        </div>
      </div>

      <Drawer isOpen={showGuide} onClose={() => setShowGuide(false)} title={<span className="text-sm font-medium text-slate-900">Hướng dẫn sử dụng</span>} size="md">
        <div className="space-y-5 text-sm text-slate-700">
          <section className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-medium text-slate-900">Mục đích</h3>
            <p className="mt-2 leading-6">
              Quy trình mẫu dùng để chuẩn hóa các công việc lặp lại. Ví dụ: tạo quy trình "Trồng cây bóng mát" rồi áp nhanh vào hạng mục "Trồng cây Sala" trong dự toán.
            </p>
          </section>

          <section>
            <h3 className="text-sm font-medium text-slate-900">Cách tạo quy trình</h3>
            <ol className="mt-3 space-y-2">
              <li>1. Nhập tên quy trình, nhóm công việc và đơn vị gốc.</li>
              <li>2. Có thể để trống mã quy trình để hệ thống tự sinh mã.</li>
              <li>3. Chọn trạng thái "Đang dùng" nếu muốn quy trình xuất hiện khi áp vào dự toán.</li>
              <li>4. Bấm "Lưu" để tạo header quy trình.</li>
              <li>5. Sau khi lưu, mở màn hình chi tiết để thêm các bước công việc và tài nguyên định mức.</li>
            </ol>
          </section>

          <section>
            <h3 className="text-sm font-medium text-slate-900">Quy ước dữ liệu</h3>
            <div className="mt-3 space-y-2">
              <p><span className="font-medium text-slate-900">Nhóm:</span> dùng để gợi ý quy trình phù hợp với hạng mục dự toán.</p>
              <p><span className="font-medium text-slate-900">Đơn vị gốc:</span> là đơn vị tính theo khối lượng hạng mục, ví dụ cây, m2, m, bộ.</p>
              <p><span className="font-medium text-slate-900">Mô tả:</span> ghi điều kiện áp dụng, phạm vi hoặc lưu ý thi công.</p>
            </div>
          </section>

          <section className="rounded-md border border-amber-200 bg-amber-50 p-4">
            <h3 className="text-sm font-medium text-amber-900">Lưu ý</h3>
            <p className="mt-2 leading-6 text-amber-800">
              Màn hình này chỉ lưu thông tin chung. Đầu mục công việc, định mức tài nguyên và đơn giá được khai báo ở màn hình chi tiết sau khi quy trình đã được tạo.
            </p>
          </section>
        </div>
      </Drawer>
    </div>
  );
}

function Field({ label, className = '', children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <label className={`space-y-1 ${className}`}>
      <span className="sm:text-sm text-xs font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
